import { PuntoVentaProps } from '../../../../interfaces/puntoVenta.ts'
import { SucursalProps } from '../../../../interfaces/sucursal.ts'

/**
 * Documentos que el frontend imprime con isi-print y cuya impresora se configura por punto de venta.
 * Los que genera el backend usan la misma referencia que apiDocRef (NOTA_VENTA, PEDIDO, CAJA...).
 * ESTADO_CUENTA y COMANDA los arma el frontend; COMANDA es la impresora de contingencia del punto
 * de venta cuando falla la impresora del área.
 * @author isi-template
 */
export const IMPRESION_DOCUMENTO = {
  FACTURA: 'FACTURA',
  NOTA_VENTA: 'NOTA_VENTA',
  PEDIDO: 'PEDIDO',
  COTIZACION: 'COTIZACION',
  CAJA: 'CAJA',
  RETIRO_CAJA: 'RETIRO_CAJA',
  DEST_ART: 'DEST_ART',
  ESTADO_CUENTA: 'ESTADO_CUENTA',
  COMANDA: 'COMANDA',
} as const

export type ImpresionDocumentoProps = (typeof IMPRESION_DOCUMENTO)[keyof typeof IMPRESION_DOCUMENTO]

/**
 * Formato de impresión, mismos valores que apiDocType (sin XML) para pedir la representación gráfica.
 * PDF = carta o medio oficio. ROLLO2, ROLLO_RESUMEN y RO_REDUCIDO solo existen para facturas.
 * Los valores son los del backend: no cambiarlos (se guardan en las asignaciones).
 */
export const IMPRESION_FORMATO = {
  ROLLO: 'ROLLO',
  PDF: 'PDF',
  ROLLO2: 'ROLLO2',
  ROLLO_RESUMEN: 'ROLLO_RESUMEN',
  /** Rollo reducido. El valor 'RO_RED' es el del backend (apiDocType) */
  RO_REDUCIDO: 'RO_RED',
} as const

export type ImpresionFormatoProps = (typeof IMPRESION_FORMATO)[keyof typeof IMPRESION_FORMATO]

/** Estructura principal de impresoras
 * @author isi-template
 * */
export interface ImpresoraProps {
  /** Identificador único del registro */
  _id: string
  atributo1: string | null
  atributo2: string | null
  createdAt: string
  descripcion: string
  nombre: string
  puntoVenta: PuntoVentaProps
  state: string
  sucursal: SucursalProps
  updatedAt: string
  usucre: string
  usumod: string
}

/**
 * Impresora, formato y copias con que un punto de venta imprime un tipo de documento.
 * puntoVenta null = valor por defecto de la sucursal. Es configuración sin historial: se borra físicamente.
 * Solo es funcional con licencia de producto IMPRESION vigente.
 * @author isi-template
 */
export interface ImpresoraAsignacionProps {
  _id: string
  copias: number
  createdAt?: Date
  documento: ImpresionDocumentoProps
  formato: ImpresionFormatoProps
  impresora: ImpresoraProps
  impresoraId: string
  /** Nombre de la impresora en isi-print de esta PC, si difiere de impresora.atributo1 (impresora compartida) */
  nombreImpresora: string | null
  /** Si es null, valor por default es para toda la sucursal */
  puntoVenta: PuntoVentaProps | null
  sucursal: SucursalProps
  updatedAt?: Date
  usucre?: string
  usumod?: string | null
}

/**
 * Asignación lista para imprimir con isi-print
 * @author isi-template
 */
export interface ImpresoraPuntoVentaProps {
  copias: number
  documento: ImpresionDocumentoProps
  formato: ImpresionFormatoProps
  impresora: ImpresoraProps
  /** Nombre a usar en isi-print: el de la asignación o impresora.atributo1 */
  nombreImpresora: string
  /** PUNTO_VENTA | SUCURSAL */
  origen: 'PUNTO_VENTA' | 'SUCURSAL'
  /** Documento cuya asignación se usó como respaldo (COMANDA usa PEDIDO); null si es propia */
  respaldo: string
}

/**
 * Parametros de entrada para la creación de un nuevo registro
 * @author isi-template
 */
export interface ApiImpresoraInputProps {
  nombre: string
  descripcion: string
  atributo1: string
  atributo2: string
}

/**
 * Asignación de impresora a un documento
 * @author isi-template
 */
export interface ApiImpresoraAsignacionInputProps {
  /** Copias por impresión, 1 a 5 (default 1) */
  copias: number
  /** FACTURA | NOTA_VENTA | PEDIDO | COTIZACION | CAJA | RETIRO_CAJA | DEST_ART | ESTADO_CUENTA | COMANDA
   * COMANDA: impresora de contingencia del punto de venta cuando falla la del área */
  documento: ImpresionDocumentoProps
  /** ROLLO (default) | PDF (carta o medio oficio). Factura también ROLLO2, ROLLO_RESUMEN, RO_REDUCIDO ('RO_RED') */
  formato: ImpresionFormatoProps
  /** Impresora de la misma sucursal */
  impresoraId: string
  /** Solo si en la PC del punto de venta la impresora tiene otro nombre en isi-print que impresora.atributo1 */
  nombreImpresora: string
}
